import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../fact-dice.css";

export type DiceFact = { text: string; to: string; linkLabel: string };

// One fact per face, each taken from copy already published on the site.
export const FACTS: Record<number, DiceFact> = {
  1: { text: "Licensed by the Betting, Gaming and Lotteries Commission as a technical service provider for gaming machines.", to: "/services", linkLabel: "See our services" },
  2: { text: "An authorised sales and service centre for Pyramid Technologies bill validators and ticket printers.", to: "/products?q=pyramid", linkLabel: "Shop Pyramid parts" },
  3: { text: "Visit us at Shop 4B, 17 Molynes Road, Kingston 10, Monday to Saturday, 9 am to 6 pm. Store pickup is free.", to: "/contact", linkLabel: "Contact & store details" },
  4: { text: "Gaming machines installed in Montego Bay, Kingston, Mandeville, St. Elizabeth and Portland.", to: "/gaming-machines#installations", linkLabel: "See our installations" },
  5: { text: "Buy gaming machines outright, lease them for your venue on a profit share, or build your own configuration.", to: "/gaming-machines", linkLabel: "Explore gaming machines" },
  6: { text: "Fault diagnosis, repairs, installation and maintenance, with a parts counter on site.", to: "/gaming-machine-repair-kingston", linkLabel: "Repairs & maintenance" },
};

const NAMES = ["", "one", "two", "three", "four", "five", "six"];
// Cells (1–9, read left to right) that hold a pip on each face.
const PIPS: Record<number, number[]> = { 1: [5], 2: [1, 9], 3: [1, 5, 9], 4: [1, 3, 7, 9], 5: [1, 3, 5, 7, 9], 6: [1, 3, 4, 6, 7, 9] };
// Cube rotation [x, y] that brings each face to the front.
export const FACE_ROTATION: Record<number, [number, number]> = { 1: [0, 0], 2: [90, 0], 3: [0, -90], 4: [0, 90], 5: [-90, 0], 6: [0, 180] };
const TILT: [number, number] = [-20, 25];
const ROLL_MS = 1350;

function reducedMotion() {
  return !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function Die({ face, turns }: { face: number; turns: number }) {
  const [x, y] = FACE_ROTATION[face];
  return (
    <div className="fd-die" style={{ transform: `rotateX(${turns * 720 + x + TILT[0]}deg) rotateY(${turns * 720 + y + TILT[1]}deg)` }}>
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <div className={`fd-face fd-${NAMES[n]}`} key={n}>
          {PIPS[n].map((cell) => (
            <i key={cell} style={{ gridArea: `${Math.ceil(cell / 3)} / ${((cell - 1) % 3) + 1}` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// About-page dice: each roll shows the facts for the two faces that land on top.
export default function FactDice({ random = Math.random }: { random?: () => number }) {
  const [faces, setFaces] = useState<[number, number]>([1, 4]);
  const [turns, setTurns] = useState(0);
  const [rolling, setRolling] = useState(false);
  const [shown, setShown] = useState<number[] | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function roll() {
    if (rolling) return;
    const still = reducedMotion();
    const next: [number, number] = [1 + Math.min(5, Math.floor(random() * 6)), 1 + Math.min(5, Math.floor(random() * 6))];
    setFaces(next);
    if (!still) setTurns((t) => t + 1);
    setRolling(true);
    setShown(null);
    timer.current = window.setTimeout(() => {
      setShown(next[0] === next[1] ? [next[0]] : next);
      setRolling(false);
    }, still ? 0 : ROLL_MS);
  }

  return (
    <section className="fd-section" aria-labelledby="fd-title">
      <div className="fd-copy">
        <p className="ab-eyebrow">SIX THINGS TO KNOW</p>
        <h2 id="fd-title">Roll for a fact.</h2>
        <p>Each face of the dice is something worth knowing about Technogenious. Roll to see two.</p>
        <button type="button" className="fd-roll" onClick={roll} disabled={rolling}>
          {rolling ? "Rolling…" : shown ? "Roll again" : "Roll the dice"}
        </button>
      </div>
      <div className="fd-play">
        <div className="fd-dice" aria-hidden="true">
          <Die face={faces[0]} turns={turns} />
          <Die face={faces[1]} turns={turns} />
        </div>
        <div className="fd-facts" aria-live="polite">
          {shown ? (
            <>
              {shown.length === 1 && <p className="fd-doubles">Doubles: one fact, twice as true.</p>}
              <ul>
                {shown.map((n) => (
                  <li key={n}>
                    <span className="fd-badge" aria-hidden="true">{n}</span>
                    <div>
                      <p>{FACTS[n].text}</p>
                      <Link to={FACTS[n].to}>{FACTS[n].linkLabel} →</Link>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="fd-prompt">{rolling ? "Rolling…" : "Press roll to see two facts."}</p>
          )}
        </div>
      </div>
    </section>
  );
}
